/*
 * Copyright (C) 2020 Airfordable, Inc - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 */

import { useLocation } from '@reach/router';
import { graphql, Link, useStaticQuery } from 'gatsby';
import React from 'react';
import { Helmet } from 'react-helmet';

import { FooterProps } from '../Footer/Footer';
import { Banners } from '../Header/Banners';
import { HeaderMode, HeaderProps } from '../Header/Header';
import { Footer } from 'components/partials/Footer';
import { Header } from 'components/partials/Header';
import { ActionableProps, Provider } from 'components/UI';
import {
  AF_ENV,
  AF_VERSION,
  NODE_ENV,
  RUNWAY_ORIGIN,
  TERMINAL_GRAPH_API_URL,
} from 'utils/constants';
const imageShare = `${RUNWAY_ORIGIN}/share.png`;

// eslint-disable-next-line no-console
console.info(`
  _____ _____ _____ _____ _____ _____ ____  _____ _____ __    _____
 |  _  |     | __  |   __|     | __  |    \\|  _  | __  |  |  |   __|
 |     |-   -|    -|   __|  |  |    -|  |  |     | __ -|  |__|   __|
 |__|__|_____|__|__|__|  |_____|__|__|____/|__|__|_____|_____|_____|

 Application: Runway
 Environment: ${AF_ENV}
 Version: ${AF_VERSION}
 Node Environment: ${NODE_ENV}
 `);

const query = graphql`
  query LayoutQuery {
    defaultYaml {
      metadata {
        title
        shortTitle
        description
        keywords
        applicationName
        canonical
      }
      navigation {
        ...Header
        ...Footer
      }
    }
  }
`;

interface LayoutData {
  defaultYaml: {
    metadata: {
      title: string;
      shortTitle: string;
      description: string;
      keywords: string;
      applicationName: string;
      canonical: string;
    };
    navigation: {
      header: HeaderProps;
      footer: FooterProps;
    };
  };
}

interface LayoutProps {
  children?: React.ReactNode;
  headerMode?: HeaderMode;
  title?: string;
  description?: string;
  keywords?: string;
  withoutFooter?: boolean;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  title,
  description,
  keywords,
  withoutFooter,
  headerMode = 'light',
}) => {
  const data = useStaticQuery<LayoutData>(query);

  const linkInstance: React.FC<ActionableProps> = ({ to, ...props }) => {
    if (!to) {
      throw Error(
        'Invalid configuration for the component. `to` must be provided.'
      );
    }
    return <Link to={to} activeClassName="menu-item__active" {...props} />;
  };

  const location = useLocation();

  const defaultTitle = data.defaultYaml.metadata.title;
  const defaultDescription = data.defaultYaml.metadata.description;
  const defaultKeywords = data.defaultYaml.metadata.keywords;
  const canonical = data.defaultYaml.metadata.canonical;
  const shortTitle = data.defaultYaml.metadata.shortTitle;

  const fullCanonical = `${canonical}${location.pathname}`;

  const pageTitle = `${title || defaultTitle} | ${shortTitle}`;
  const pageDescription = description || defaultDescription;
  const pageKeywords = keywords || defaultKeywords;

  return (
    <Provider>
      <Helmet>
        <html lang="en" />

        {/* Default */}
        <meta
          name="application-name"
          content={data.defaultYaml.metadata.applicationName}
        />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />

        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url" content={fullCanonical} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={imageShare} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={imageShare} />

        <link rel="preconnect" href={TERMINAL_GRAPH_API_URL} />
      </Helmet>

      <Banners />
      <Header
        linkInstance={linkInstance}
        menu={data.defaultYaml.navigation.header.menu}
        menuLabeled={data.defaultYaml.navigation.header.menuLabeled}
        mode={headerMode}
      />

      {children}

      {!withoutFooter && (
        <Footer
          linkInstance={linkInstance}
          copyright={data.defaultYaml.navigation.footer.copyright}
          footerNote={data.defaultYaml.navigation.footer.footerNote}
          menu={data.defaultYaml.navigation.footer.menu}
          social={data.defaultYaml.navigation.footer.social}
          preFooter={data.defaultYaml.navigation.footer.preFooter}
        />
      )}
    </Provider>
  );
};

export default Layout;
